import { defineStore } from "pinia";
import { ref } from "vue";
import getUser from "@/graphql/queries/getUser.graphql";
import { normalizeMacAddress } from "@/Common/StringOperations";

export const usePermissionsStore = defineStore('permissions', () => {
  const permissions = ref<string[]>([]);
  const loading = ref(false);
  const isSuperAdmin = ref(false);
  const hasTenantAdminRole = ref(false);
  function hasWriteWildcard() {
    if (isSuperAdmin.value) return true;
    return permissions.value.includes('write:');
  }

  function hasReadWildcard() {
    if (isSuperAdmin.value) return true;
    return permissions.value.includes('read:');
  }

  function hasExactPermission(permission: string) {
    if (isSuperAdmin.value) return true;
    if (permission.startsWith('write:')) {
      return hasWriteWildcard() || permissions.value.includes(permission);
    }
    if (permission.startsWith('read:')) {
      return hasWriteWildcard() || hasReadWildcard() || permissions.value.includes(permission);
    }
    return permissions.value.includes(permission);
  }

  function hasGlobalWildcard() {
    if (isSuperAdmin.value) return true;
    return hasWriteWildcard() || hasReadWildcard();
  }

  // For config action in group menu: requires both read:config (any scope) AND write:clientGroup
  function canShowConfigAction() {
    if (hasGlobalWildcard()) return true;
    const hasReadConfig = permissions.value.some(
      (perm: string) => perm.startsWith('read:config')
    );
    const hasWriteClientGroup = permissions.value.some(
      (perm: string) => perm.startsWith('write:clientGroup')
    );
    return hasReadConfig && hasWriteClientGroup;
  }

  // tenant settings are visible for tenant_admin users or users with activate-clients write permission
  function canShowSettings() {
    if (isSuperAdmin.value) return true;
    return hasTenantAdminRole.value || hasExactPermission('write:activateClients');
  }

  // Policy-matching menu permission checks
  function canShowUsersMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:user or write:user or read:user:self
    else
    return permissions.value.some(
      (perm: string) => perm === 'read:user' || perm === 'write:user' || perm === 'read:user:self'
    );
  }

  function canShowRolesMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:role or write:role or any role-specific permission
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:role') || perm.startsWith('write:role')
    );
  }

  function canShowConfigMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has any read:config or write:config permission
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:config') || perm.startsWith('write:config')
    );
  }

  // Strict config menu: requires both read:config (any scope) AND write:clientGroup
  function canShowConfigMenuStrict() {
    if (hasGlobalWildcard()) return true;
    const hasReadConfig = permissions.value.some(
      (perm: string) => perm.startsWith('read:config')
    );
    const hasWriteClientGroup = permissions.value.some(
      (perm: string) => perm.startsWith('write:clientGroup')
    );
    return hasReadConfig && hasWriteClientGroup;
  }

  function canShowUserGroupsMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:userGroup or write:userGroup or any userGroup-specific permission
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:userGroup') || perm.startsWith('write:userGroup')
    );
  }

  function canShowClientGroupsMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:clientGroup or write:clientGroup or any clientGroup-specific permission
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:clientGroup') || perm.startsWith('write:clientGroup')
    );
  }

  function canShowTenantsMenu() {
    return isSuperAdmin.value;
  }

  function canShowConfigProfilesMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:configProfile or write:configProfile
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:configProfile') || perm.startsWith('write:configProfile')
    );
  }

  function canShowAuditLogMenu() {
    if (hasGlobalWildcard()) return true;
    // Show if user has read:auditLog or write:auditLog
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:auditLog')
    );
  }
      // Policy-matching permission checks
  function canReadUserGroup(groupName?: string) {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some(
      (perm: string) => perm === 'read:userGroup' || (groupName && perm === `read:userGroup:${groupName}`)
    );
  }

  function canReadClientGroup(groupName?: string) {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some(
      (perm: string) =>
        perm === 'read:clientGroup' ||
        perm === 'write:clientGroup' ||
        (groupName && (perm === `read:clientGroup:${groupName}` || perm === `write:clientGroup:${groupName}`))
    );
  }

  function canReadRole(roleName?: string) {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some(
      (perm: string) => perm === 'read:role' || (roleName && perm === `read:role:${roleName}`)
    );
  }

  function canReadConfigProfile() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some((perm: string) => perm === 'read:configProfile');
  }

  function canReadAuditLog() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some((perm: string) => perm === 'read:auditLog');
  }

  function canReadUser() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some((perm: string) => perm === 'read:user');
  }

  function setPermissions(newPermissions: string[]) {
    permissions.value = newPermissions;
  }

  async function fetchPermissions(userId: number) {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery(
        {
          query: getUser,
          variables: {
            id: userId
          },
        }
      );
      if (res?.data?.getUser?.isSuperAdmin) {
        isSuperAdmin.value = true;
        hasTenantAdminRole.value = true;
        return;
      }

      const allPermissions: string[] = [];

      // If getUser is null (forbidden), fallback to minimal permissions
      if (!res?.data?.getUser) {
        permissions.value = ['read:user:self', 'read:role'];
        hasTenantAdminRole.value = false;
        return;
      }

      hasTenantAdminRole.value = Array.isArray(res.data.getUser.roles)
        && res.data.getUser.roles.some((role: any) => role?.name === 'tenant_admin');

      // Get permissions from roles
      if (res.data.getUser.roles) {
        const rolePermissions = res.data.getUser.roles
          .flatMap((role: any) => Array.isArray(role.permissions) ? role.permissions : [])
          .filter((p: any) => typeof p === 'string' && p.length > 0);
        allPermissions.push(...rolePermissions);
      }

      // Get permissions from user groups
      if (res.data.getUser.userGroups) {
        const groupPermissions = res.data.getUser.userGroups
          .flatMap((group: any) => Array.isArray(group.permissions) ? group.permissions : [])
          .filter((p: any) => typeof p === 'string' && p.length > 0);
        allPermissions.push(...groupPermissions);
      }

      // Remove duplicates and set permissions
      permissions.value = Array.from(new Set(allPermissions));
    } finally {
      loading.value = false;
    }
  }

  function canWrite(resource: string, groupName?: string) {
    if (hasWriteWildcard()) return true;
    let normalizedGroupName = groupName;
    if (resource === 'client' && groupName) {
      normalizedGroupName = normalizeMacAddress(groupName);
    }
    const requiredPath = [resource, ...(normalizedGroupName ? [normalizedGroupName] : [])];
    return hasPermission('write', requiredPath);
  }

  function canRead(resource: string, groupName?: string) {
    if (hasGlobalWildcard()) return true;
    // read:user:self must not imply list/read access for all users.
    if (resource === 'user' && !groupName) {
      return permissions.value.some(
        (perm: string) => perm === 'read:user' || perm === 'write:user'
      );
    }
    const requiredPath = [resource, ...(groupName ? [groupName] : [])];
    return hasPermission('read', requiredPath);
  }

  function hasPermission(requiredAction: 'read' | 'write', requiredPath: string[]) {
    return permissions.value.some((perm) => matchesPermission(perm, requiredAction, requiredPath));
  }

  function matchesPermission(
    permission: string,
    requiredAction: 'read' | 'write',
    requiredPath: string[],
  ) {
    // Global wildcard: 'write:' matches everything, 'read:' matches all reads
    if (permission === 'write:') {
      // write: matches all write and read permissions
      return requiredAction === 'write' || requiredAction === 'read';
    }
    if (permission === 'read:') {
      // read: matches all read permissions
      return requiredAction === 'read';
    }

    const parts = permission.split(':').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return false;

    const userAction = parts[0];
    const userPath = parts.slice(1);

    // Debug output
    // console.log('[DEBUG][matchesPermission]', {
    //   permission,
    //   requiredAction,
    //   requiredPath,
    //   userAction,
    //   userPath
    // });

    if (requiredAction === 'write') {
      // Only write:... matches write
      return userAction === 'write' && isPathMatch(userPath, requiredPath);
    }
    if (requiredAction === 'read') {
      // write implies read
      return (userAction === 'read' || userAction === 'write') && isPathMatch(userPath, requiredPath);
    }

    return false;
  }

  function isPathMatch(userPath: string[], requiredPath: string[]) {
    // User path must be at least as long as required path
    if (userPath.length < requiredPath.length) {
      return false;
    }
    // Check if required path is a prefix of user path
    for (let i = 0; i < requiredPath.length; i += 1) {
      if (userPath[i] !== requiredPath[i]) {
        return false;
      }
    }
    return true;
  }

  // Accepts any type for client/group to avoid TS errors if not imported
  function canViewClientActions() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some((perm: string) => perm.startsWith('write:client') || perm.startsWith('write:clientGroup'));
  }

  function canViewGroupContextMenu() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some((perm: string) => perm.startsWith('write:clientGroup'));
  }

  function canViewClients() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some(
      (perm: string) =>
        perm.startsWith('read:client') ||
        perm.startsWith('write:client') ||
        perm.startsWith('read:clientGroup') ||
        perm.startsWith('write:clientGroup') ||
        perm.startsWith('read:client:') ||
        perm.startsWith('write:client:')
    );
  }

  // Checks for exact per-client write permission
  function hasClientWritePermission(mac?: string) {
    if (hasWriteWildcard()) return true;
    if (!mac) return false;
    const normalizedMac = normalizeMacAddress(mac);
    return permissions.value.includes(`write:client:${normalizedMac}`);
  }

  function canCreateClientGroup() {
    if (hasWriteWildcard()) return true;
    else
    return permissions.value.includes('write:clientGroup');
  }
  function canCreateRole() {
    if (hasWriteWildcard()) return true;
    else
    return permissions.value.includes('write:role');
  }
  function canCreateUserGroup() {
    if (hasWriteWildcard()) return true;
    else
    return permissions.value.includes('write:userGroup');
  }

  function canCreateUser() {
    if (hasWriteWildcard()) return true;
    else
    return permissions.value.includes('write:user')
  }

  /**
   * Returns the path of the first accessible page for the current user.
   * The order matches the main navigation/menu structure.
   */
  function getFirstAccessibleRoute() {
    // Prioritize clients page if accessible
    if (canViewClients()) {
      return '/clients';
    }
    if (canShowRolesMenu()) {
      return '/roles';
    }
    if (canShowConfigMenu()) {
      return '/configProfile';
    }
    if (canShowAuditLogMenu()) {
      return '/audit-log';
    }
    if (canShowUsersMenu()) {
      // Users with only read:user:self cannot list users — send them straight to their own profile
      if (!canRead('user') && permissions.value.includes('read:user:self')) {
        return '/users/me';
      }
      return '/users';
    }
    if (canShowUserGroupsMenu()) {
      return '/userGroups';
    }
    if(canShowTenantsMenu()) {
      return '/tenants';
    }
    if(canShowSettings()) {
      return '/settings';
    }
    return null;
  }

  function canViewScheduledActions() {
    if (hasGlobalWildcard()) return true;
    return permissions.value.some(
      (perm: string) => perm.startsWith('read:scheduledActions') || perm.startsWith('write:scheduledActions')
    );
  }

  /**
   * Checks if the user can schedule an action for a given group or client.
   * Accepts groupName or macAddress and optionally actionType for granularity.
   */
  function canScheduleAction({ groupName, macAddress, actionType }: { groupName?: string, macAddress?: string, actionType?: string }) {
    if (hasGlobalWildcard()) return true;

    const normalizedMac = macAddress ? normalizeMacAddress(macAddress) : undefined;
    const hasWritePermission = (permission: string) =>
      permissions.value.includes('write:') || permissions.value.includes(permission);

    const hasClientWritePermission =
      hasWritePermission('write:client') ||
      (normalizedMac ? hasWritePermission(`write:client:${normalizedMac}`) : false) ||
      (groupName ? hasWritePermission(`write:clientGroup:${groupName}`) : false);
    const hasClientGroupWritePermission =
      hasWritePermission('write:clientGroup') ||
      (groupName ? hasWritePermission(`write:clientGroup:${groupName}`) : false);

    if (!hasClientWritePermission && !hasClientGroupWritePermission) return false;

    // Most specific: clientGroup + actionType
    if (groupName && actionType && hasWritePermission(`write:scheduledActions:clientGroup:${groupName}:${actionType}`)) return true;
    // Most specific: client + actionType
    if (normalizedMac && actionType && hasWritePermission(`write:scheduledActions:client:${normalizedMac}:${actionType}`)) return true;
    // Less specific: clientGroup only
    if (groupName && hasWritePermission(`write:scheduledActions:clientGroup:${groupName}`)) return true;
    // Less specific: client only
    if (normalizedMac && hasWritePermission(`write:scheduledActions:client:${normalizedMac}`)) return true;
    // General clientGroup scoped
    if (hasWritePermission('write:scheduledActions:clientGroup')) return true;
    // General client scoped
    if (hasWritePermission('write:scheduledActions:client')) return true;
    // General permission
    if (hasWritePermission('write:scheduledActions')) return true;

    return false;
  }

  /**
   * Checks if the user can execute a client action.
   * Supports global, action-specific, target-specific, and combined granular permissions.
   */
  function canExecuteAction({
    actionType,
    groupName,
    macAddress,
  }: {
    actionType: string,
    groupName?: string,
    macAddress?: string,
  }) {
    if (hasGlobalWildcard()) return true;

    const normalizedMac = macAddress ? normalizeMacAddress(macAddress) : undefined;
    const hasWritePermission = (permission: string) =>
      permissions.value.includes('write:') || permissions.value.includes(permission);

    const hasClientWritePermission =
      hasWritePermission('write:client') ||
      (normalizedMac ? hasWritePermission(`write:client:${normalizedMac}`) : false) ||
      (groupName ? hasWritePermission(`write:clientGroup:${groupName}`) : false);

    const hasClientGroupWritePermission =
      hasWritePermission('write:clientGroup') ||
      (groupName ? hasWritePermission(`write:clientGroup:${groupName}`) : false);

    if (!hasClientWritePermission && !hasClientGroupWritePermission) return false;

    if (normalizedMac && hasWritePermission(`write:action:client:${normalizedMac}:${actionType}`)) return true;
    if (groupName && hasWritePermission(`write:action:clientGroup:${groupName}:${actionType}`)) return true;

    if (hasWritePermission(`write:action:client:${actionType}`)) return true;
    if (hasWritePermission(`write:action:clientGroup:${actionType}`)) return true;

    if (normalizedMac && hasWritePermission(`write:action:client:${normalizedMac}`)) return true;
    if (groupName && hasWritePermission(`write:action:clientGroup:${groupName}`)) return true;

    if (hasWritePermission('write:action:client')) return true;
    if (hasWritePermission('write:action:clientGroup')) return true;
    if (hasWritePermission(`write:action:${actionType}`)) return true;
    if (hasWritePermission('write:action')) return true;

    return false;
  }

  return  {
    canShowConfigAction,
    canShowSettings,
    canShowUsersMenu,
    canShowRolesMenu,
    canShowConfigMenu,
    canShowConfigMenuStrict,
    canShowUserGroupsMenu,
    canShowClientGroupsMenu,
    canShowConfigProfilesMenu,
    canShowAuditLogMenu,
    permissions,
    loading,
    setPermissions,
    fetchPermissions,
    canWrite,
    canRead,
    canReadUserGroup,
    canReadClientGroup,
    canReadRole,
    canReadConfigProfile,
    canReadAuditLog,
    canReadUser,
    canViewClientActions,
    canViewGroupContextMenu,
    canViewClients,
    hasExactPermission,
    hasClientWritePermission,
    canCreateClientGroup,
    canCreateRole,
    canCreateUserGroup,
    getFirstAccessibleRoute,
    canCreateUser,
    canViewScheduledActions,
    canScheduleAction,
    canExecuteAction,
    canShowTenantsMenu,
    isSuperAdmin,
  };
});
